import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  Phone, PhoneIncoming, Calendar, CalendarCheck, CalendarX, UserCheck, 
  Search, RefreshCw, User, Clock, Building, Mail, MapPin, 
  GripVertical, ExternalLink, Eye, EyeOff, Wrench, Users, CheckSquare, 
  Truck, FileCheck, Receipt, CreditCard, CheckCircle2, Briefcase, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================
// STAGE CONFIGURATIONS (Builder Prime Aligned)
// ============================================

// Call Center Intake Stages (matches Builder Prime)
const CALL_CENTER_STAGES = [
  { id: 'lead_received', name: 'Lead Received', color: 'bg-slate-500', icon: PhoneIncoming },
  { id: 'lead_contacted', name: 'Contacted', color: 'bg-blue-500', icon: Phone },
  { id: 'lead_self_booked', name: 'Self Booked', color: 'bg-cyan-500', icon: Calendar },
  { id: 'lead_needs_self_book', name: 'Needs Self-Book', color: 'bg-purple-500', icon: Calendar },
  { id: 'appointment_not_set', name: 'Appt Not Set', color: 'bg-yellow-500', icon: CalendarX },
  { id: 'appointment_set', name: 'Appt Set', color: 'bg-indigo-500', icon: CalendarCheck },
  { id: 'appointment_cancelled', name: 'Appt Cancelled', color: 'bg-orange-500', icon: CalendarX },
  { id: 'appointment_confirmed', name: 'Appt Confirmed', color: 'bg-green-500', icon: CalendarCheck },
  { id: 'nurture', name: 'Nurture', color: 'bg-pink-500', icon: UserCheck },
  { id: 'existing_customer', name: 'Existing Customer', color: 'bg-teal-500', icon: UserCheck },
  { id: 'dead', name: 'Dead', color: 'bg-slate-400', icon: CalendarX, isArchive: true },
];

// Sales Flow Stages (matches Builder Prime)
const SALES_STAGES = [
  { id: 'lead_issued', name: 'Lead Issued', color: 'bg-blue-500', icon: User },
  { id: 'reset_needs_appointment', name: 'Reset - Needs Appt', color: 'bg-slate-500', icon: CalendarX },
  { id: 'reset_with_appointment', name: 'Reset - w/ Appt', color: 'bg-indigo-500', icon: CalendarCheck },
  { id: 'reset_appt_cancelled', name: 'Reset - Cancelled', color: 'bg-orange-500', icon: CalendarX },
  { id: 'no_demo', name: 'No Demo', color: 'bg-yellow-500', icon: CalendarX },
  { id: 'demo', name: 'Demo', color: 'bg-purple-500', icon: CalendarCheck },
  // Project sales stages moved from Ops: Estimate Sent → Estimate Request → Customer Signed
  { id: 'estimate_sent', name: 'Estimate Sent', color: 'bg-cyan-500', icon: Briefcase },
  { id: 'estimate_request', name: 'Estimate Request', color: 'bg-slate-500', icon: Briefcase },
  { id: 'customer_signed', name: 'Customer Signed', color: 'bg-indigo-500', icon: CheckCircle2 },
  // Sales outcomes
  { id: 'job_sold', name: 'Job Sold', color: 'bg-emerald-500', icon: CheckCircle2 },
  { id: 'job_in_progress', name: 'In Progress', color: 'bg-amber-500', icon: Wrench },
  { id: 'customer', name: 'Customer', color: 'bg-teal-500', icon: UserCheck },
  { id: 'demo_no_sale', name: 'No Sale', color: 'bg-slate-400', icon: CalendarX, isArchive: true },
  { id: 'credit_reject', name: 'Credit Reject', color: 'bg-red-500', icon: CalendarX, isArchive: true },
];

// Operations Production Stages (matches Builder Prime Projects - sales stages removed)
const OPS_STAGES = [
  { id: 'sold', name: 'Sold', color: 'bg-purple-500', icon: CheckCircle2 },
  { id: 'deposit_received', name: 'Deposit Received', color: 'bg-cyan-500', icon: CreditCard },
  { id: 'sale_contingent', name: 'Sale Contingent', color: 'bg-pink-500', icon: Briefcase },
  { id: 'contacted', name: 'Contacted', color: 'bg-teal-500', icon: Phone },
  { id: 'scheduled', name: 'Scheduled', color: 'bg-amber-500', icon: Calendar },
  { id: 'in_progress', name: 'In Progress', color: 'bg-orange-500', icon: Truck },
  { id: 'collections', name: 'Collections', color: 'bg-yellow-500', icon: Receipt },
  { id: 'complete', name: 'Complete', color: 'bg-green-600', icon: CheckCircle2 },
  { id: 'recoating', name: 'Recoating', color: 'bg-green-500', icon: Wrench },
  { id: 'sale_lost', name: 'Sale Lost', color: 'bg-slate-400', icon: CalendarX, isArchive: true },
  { id: 'cancelled', name: 'Cancelled', color: 'bg-red-500', icon: CalendarX, isArchive: true },
];

// Sales Shadow Tracking Stages (read-only in Full Flow View)
const SALES_TRACKING_STAGES = [
  { id: 'sold', name: 'Sold', color: 'bg-emerald-500', icon: CheckCircle2, description: 'Sale closed, tracking active' },
  { id: 'completed', name: 'Completed', color: 'bg-teal-500', icon: CheckCircle2, description: 'Current year completions' },
  { id: 'archived', name: 'Archived', color: 'bg-slate-400', icon: CheckCircle2, description: 'Prior year sales', isArchive: true },
];

// Stage to Lane mapping (expanded for Builder Prime)
const CALL_CENTER_STAGE_IDS = [
  'lead_received', 'lead_contacted', 'lead_self_booked', 'lead_needs_self_book',
  'appointment_not_set', 'appointment_set', 'appointment_cancelled', 'appointment_confirmed', 
  'nurture', 'existing_customer', 'dead', 'temp_marketing'
];
const SALES_STAGE_IDS = [
  'lead_issued', 'lead_assigned', 'reset_needs_appointment', 'reset_with_appointment', 
  'reset_appt_cancelled', 'no_demo', 'demo', 'demo_confirmed', 'estimate_sent',
  'job_sold', 'demo_sold', 'job_in_progress', 'customer', 'demo_no_sale', 
  'credit_reject', 'job_sold_cancelled', 'sold', 'signed', 'qualified', 'pending_signature'
];
const OPS_STAGE_IDS = [
  'estimate_request', 'estimate_sent', 'customer_signed', 'sold', 'deposit_received',
  'contacted', 'scheduled', 'in_progress', 'collections', 'complete', 'recoating',
  'sale_contingent', 'sale_lost', 'cancelled', 'production', 'crew_assigned', 
  'install', 'qc', 'inspections', 'invoice', 'pending_payment', 'paid'
];

// Determine which lane a record belongs to
const getLane = (record) => {
  const stage = record.stage || record.status || 'lead_received';
  
  if (CALL_CENTER_STAGE_IDS.includes(stage)) return 'callcenter';
  if (SALES_STAGE_IDS.includes(stage)) return 'sales';
  if (OPS_STAGE_IDS.includes(stage)) return 'ops';
  
  // Default based on assignment and type
  if (record.recordType === 'project') return 'ops';
  if (!record.assigned_to) return 'callcenter';
  return 'sales';
};

// Map to normalized stage IDs (Builder Prime aligned)
const normalizeStage = (record, lane) => {
  const stage = record.stage || record.status || 'lead_received';
  
  if (lane === 'callcenter') {
    // Direct mapping for Call Center stages
    const ccMapping = {
      'new': 'lead_received',
      'lead': 'lead_received',
      'lead_recieved': 'lead_received',
      'contacted': 'lead_contacted',
      'callback': 'appointment_not_set',
    };
    return ccMapping[stage] || (CALL_CENTER_STAGES.some(s => s.id === stage) ? stage : 'lead_received');
  }
  
  if (lane === 'sales') {
    // First check bp_status_name for accurate mapping, then fall back to status field
    const bpStatus = record.bp_status_name || record.status || '';
    const bpSalesMapping = {
      'Lead Issued': 'lead_issued',
      'Reset - Needs Appointment': 'reset_needs_appointment',
      'Reset - w/ Appointment': 'reset_with_appointment',
      'Reset - Appt Cancelled': 'reset_appt_cancelled',
      'No Demo': 'no_demo',
      'Demo': 'demo',
      'Demo / No Sale': 'demo_no_sale',
      'Estimate Sent': 'estimate_sent',
      'Estimate Request': 'estimate_request',
      'Customer Signed': 'customer_signed',
      'Job Sold': 'job_sold',
      'Job In Progress': 'job_in_progress',
      'Customer': 'customer',
      'Credit Reject': 'credit_reject',
    };
    if (bpStatus && bpSalesMapping[bpStatus]) {
      return bpSalesMapping[bpStatus];
    }
    
    // Fallback to stage mapping
    const salesMapping = {
      'lead_assigned': 'lead_issued',
      'intro_call': 'lead_issued',
      'qualified': 'lead_issued',
      'demo_scheduled': 'demo',
      'appointment_set': 'demo',
      'demo_confirmed': 'demo',
      'demo_completed': 'demo',
      'pending_signature': 'customer_signed',
      'proposal_sent': 'estimate_sent',
      'sold': 'job_sold',
      'demo_sold': 'job_sold',
      'signed': 'customer_signed',
      'signed_agreement': 'customer_signed',
      'contract_signed': 'customer_signed',
      'to_production': 'job_in_progress',
      'production': 'job_in_progress',
      'lost': 'demo_no_sale',
      'lost_dnq': 'demo_no_sale',
      'no_sale': 'demo_no_sale',
    };
    return salesMapping[stage] || (SALES_STAGES.some(s => s.id === stage) ? stage : 'lead_issued');
  }
  
  if (lane === 'ops') {
    // First check bp_status_name for accurate mapping, then fall back to status field
    const bpStatus = record.bp_status_name || record.status || '';
    const bpOpsMapping = {
      'Sold': 'sold',
      'Deposit Received': 'deposit_received',
      'Sale Contingent': 'sale_contingent',
      'Contacted': 'contacted',
      'Scheduled': 'scheduled',
      'In Progress': 'in_progress',
      'Collections (Complete, Need Payment)': 'collections',
      'Complete': 'complete',
      'Recoating': 'recoating',
      'Sale Lost': 'sale_lost',
      'Cancelled': 'cancelled',
    };
    if (bpStatus && bpOpsMapping[bpStatus]) {
      return bpOpsMapping[bpStatus];
    }
    
    // Fallback to stage mapping
    const opsMapping = {
      'production': 'in_progress',
      'crew_assigned': 'scheduled',
      'install': 'in_progress',
      'qc': 'in_progress',
      'final_inspection': 'in_progress',
      'invoice': 'collections',
      'invoiced': 'collections',
      'pending_payment': 'collections',
      'paid': 'complete',
    };
    return opsMapping[stage] || (OPS_STAGES.some(s => s.id === stage) ? stage : 'deposit_received');
  }
  
  return stage;
};

// Calculate days in current status
const getDaysInStatus = (record) => {
  if (!record.stage_changed_at && !record.created_at) return 0;
  const stageDate = new Date(record.stage_changed_at || record.created_at);
  const now = new Date();
  return Math.floor((now - stageDate) / (1000 * 60 * 60 * 24));
};

// ============================================
// SWIMLANE CARD COMPONENT
// ============================================

const SwimLaneCard = ({ record, lane, index, onClick }) => {
  // Vibrant priority colors with left border
  const priorityColors = {
    urgent: 'border-l-4 border-l-rose-500 bg-rose-50/50 dark:bg-rose-900/10',
    high: 'border-l-4 border-l-red-500 bg-red-50/30 dark:bg-red-900/10',
    medium: 'border-l-4 border-l-amber-500 bg-amber-50/30 dark:bg-amber-900/10',
    low: 'border-l-4 border-l-emerald-500 bg-emerald-50/30 dark:bg-emerald-900/10',
  };

  const name = record.contact_name || record.customer_name || 'Unknown';
  const daysInStage = getDaysInStatus(record);
  
  // Get value color based on amount
  const getValueColor = (value) => {
    if (!value) return 'text-slate-500';
    if (value >= 10000) return 'text-emerald-600 dark:text-emerald-400 font-bold';
    if (value >= 5000) return 'text-blue-600 dark:text-blue-400 font-semibold';
    return 'text-slate-600 dark:text-slate-400';
  };

  return (
    <Draggable draggableId={`${lane}-${record.id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={() => onClick(record, lane)}
          className={`bg-white dark:bg-slate-800 rounded-lg p-2.5 mb-2 shadow-sm cursor-pointer border border-slate-200 dark:border-slate-700 transition-all ${
            priorityColors[record.priority] || 'border-l-4 border-l-slate-300'
          } ${
            snapshot.isDragging 
              ? 'shadow-xl ring-2 ring-blue-400 rotate-1 scale-105' 
              : 'hover:shadow-md hover:border-blue-300 dark:hover:border-blue-600 hover:-translate-y-0.5'
          }`}
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-1 mb-1.5">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded" onClick={(e) => e.stopPropagation()}>
                <GripVertical className="h-3 w-3 text-slate-400" />
              </div>
              <span className="font-medium text-xs truncate text-slate-800 dark:text-slate-100">{name}</span>
            </div>
            <ExternalLink className="h-3 w-3 text-blue-500 hover:text-blue-600 flex-shrink-0" />
          </div>
          
          {/* Details */}
          <div className="space-y-1 text-[10px]">
            {record.address && (
              <div className="flex items-center gap-1 truncate text-slate-500 dark:text-slate-400">
                <MapPin className="h-2.5 w-2.5 flex-shrink-0 text-slate-400" />
                <span className="truncate">{record.address}</span>
              </div>
            )}
            {record.estimated_value && (
              <div className="flex items-center gap-1">
                <span className={getValueColor(record.estimated_value)}>
                  ${record.estimated_value.toLocaleString()}
                </span>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between mt-1.5 pt-1 border-t border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-1 text-[10px] text-slate-400">
              <Clock className="h-2.5 w-2.5" />
              <span>{daysInStage}d</span>
            </div>
            {record.recordType === 'project' && (
              <Badge className="text-[8px] px-1.5 py-0 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300 border-0">
                Project
              </Badge>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
};

// ============================================
// SWIMLANE COLUMN COMPONENT
// ============================================

const SwimLaneColumn = ({ stage, records, lane, onCardClick }) => {
  const Icon = stage.icon;
  const stageRecords = records.filter(r => normalizeStage(r, lane) === stage.id);
  
  // Get a subtle border color instead of full background
  const getBorderColor = (color) => {
    const colorMap = {
      'bg-slate-500': 'border-slate-400',
      'bg-slate-400': 'border-slate-300',
      'bg-blue-500': 'border-blue-400',
      'bg-cyan-500': 'border-cyan-400',
      'bg-purple-500': 'border-purple-400',
      'bg-yellow-500': 'border-yellow-400',
      'bg-indigo-500': 'border-indigo-400',
      'bg-orange-500': 'border-orange-400',
      'bg-green-500': 'border-green-400',
      'bg-green-600': 'border-green-500',
      'bg-pink-500': 'border-pink-400',
      'bg-teal-500': 'border-teal-400',
      'bg-amber-500': 'border-amber-400',
      'bg-emerald-500': 'border-emerald-400',
      'bg-red-500': 'border-red-400',
    };
    return colorMap[color] || 'border-slate-300';
  };
  
  const getTextColor = (color) => {
    const colorMap = {
      'bg-slate-500': 'text-slate-600 dark:text-slate-300',
      'bg-slate-400': 'text-slate-500 dark:text-slate-400',
      'bg-blue-500': 'text-blue-600 dark:text-blue-400',
      'bg-cyan-500': 'text-cyan-600 dark:text-cyan-400',
      'bg-purple-500': 'text-purple-600 dark:text-purple-400',
      'bg-yellow-500': 'text-yellow-600 dark:text-yellow-400',
      'bg-indigo-500': 'text-indigo-600 dark:text-indigo-400',
      'bg-orange-500': 'text-orange-600 dark:text-orange-400',
      'bg-green-500': 'text-green-600 dark:text-green-400',
      'bg-green-600': 'text-green-700 dark:text-green-400',
      'bg-pink-500': 'text-pink-600 dark:text-pink-400',
      'bg-teal-500': 'text-teal-600 dark:text-teal-400',
      'bg-amber-500': 'text-amber-600 dark:text-amber-400',
      'bg-emerald-500': 'text-emerald-600 dark:text-emerald-400',
      'bg-red-500': 'text-red-600 dark:text-red-400',
    };
    return colorMap[color] || 'text-slate-600 dark:text-slate-300';
  };
  
  const getBgColor = (color) => {
    const colorMap = {
      'bg-slate-500': 'bg-slate-50 dark:bg-slate-900/50',
      'bg-slate-400': 'bg-slate-50 dark:bg-slate-900/50',
      'bg-blue-500': 'bg-blue-50 dark:bg-blue-900/20',
      'bg-cyan-500': 'bg-cyan-50 dark:bg-cyan-900/20',
      'bg-purple-500': 'bg-purple-50 dark:bg-purple-900/20',
      'bg-yellow-500': 'bg-yellow-50 dark:bg-yellow-900/20',
      'bg-indigo-500': 'bg-indigo-50 dark:bg-indigo-900/20',
      'bg-orange-500': 'bg-orange-50 dark:bg-orange-900/20',
      'bg-green-500': 'bg-green-50 dark:bg-green-900/20',
      'bg-green-600': 'bg-green-50 dark:bg-green-900/20',
      'bg-pink-500': 'bg-pink-50 dark:bg-pink-900/20',
      'bg-teal-500': 'bg-teal-50 dark:bg-teal-900/20',
      'bg-amber-500': 'bg-amber-50 dark:bg-amber-900/20',
      'bg-emerald-500': 'bg-emerald-50 dark:bg-emerald-900/20',
      'bg-red-500': 'bg-red-50 dark:bg-red-900/20',
    };
    return colorMap[color] || 'bg-slate-50 dark:bg-slate-900/50';
  };
  
  return (
    <div className={`flex-shrink-0 w-36 ${stage.isArchive ? 'opacity-60' : ''}`}>
      {/* Refined header - subtle background with colored left border */}
      <div className={`${getBgColor(stage.color)} border-l-4 ${getBorderColor(stage.color)} rounded-t-lg px-2 py-1.5`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <Icon className={`h-3 w-3 ${getTextColor(stage.color)}`} />
            <span className={`font-semibold text-[10px] truncate ${getTextColor(stage.color)}`}>{stage.name}</span>
          </div>
          <Badge variant="outline" className={`${getTextColor(stage.color)} border-current text-[9px] px-1 py-0`}>
            {stageRecords.length}
          </Badge>
        </div>
      </div>
      <Droppable droppableId={`${lane}-${stage.id}`}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`bg-slate-100/50 dark:bg-slate-900/50 rounded-b-lg p-1.5 min-h-[180px] max-h-[220px] overflow-y-auto transition-colors ${
              snapshot.isDraggingOver ? 'bg-primary/10 ring-2 ring-inset ring-primary' : ''
            }`}
          >
            {stageRecords.map((record, index) => (
              <SwimLaneCard 
                key={record.id} 
                record={record} 
                lane={lane}
                index={index} 
                onClick={onCardClick} 
              />
            ))}
            {stageRecords.length === 0 && !snapshot.isDraggingOver && (
              <div className="text-center py-4 text-muted-foreground text-[10px]">Empty</div>
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

// ============================================
// SWIMLANE ROW COMPONENT
// ============================================

const SwimLaneRow = ({ title, subtitle, badge, stages, records, lane, onCardClick, showNoSale, color }) => {
  const visibleStages = showNoSale ? stages : stages.filter(s => !s.isArchive);
  
  // Get lane-specific accent colors
  const getLaneAccent = (lane) => {
    const accents = {
      'callcenter': { border: 'border-l-slate-400', bg: 'bg-slate-50 dark:bg-slate-900/50', text: 'text-slate-700 dark:text-slate-200' },
      'sales': { border: 'border-l-blue-500', bg: 'bg-blue-50/50 dark:bg-blue-900/20', text: 'text-blue-700 dark:text-blue-300' },
      'ops': { border: 'border-l-amber-500', bg: 'bg-amber-50/50 dark:bg-amber-900/20', text: 'text-amber-700 dark:text-amber-300' },
    };
    return accents[lane] || accents.callcenter;
  };
  
  const accent = getLaneAccent(lane);
  
  return (
    <div className={`border rounded-lg overflow-hidden bg-white dark:bg-slate-900 shadow-sm`}>
      {/* Lane Header - Refined with left accent */}
      <div className={`${accent.bg} border-l-4 ${accent.border} px-4 py-2.5 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <h3 className={`font-semibold text-sm ${accent.text}`}>{title}</h3>
          {badge && (
            <Badge variant="outline" className={`text-[10px] ${accent.text} border-current`}>{badge}</Badge>
          )}
        </div>
        <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">{subtitle}</span>
      </div>
      
      {/* Lane Columns */}
      <div className="p-3 overflow-x-auto">
        <div className="flex gap-2">
          {visibleStages.map(stage => (
            <SwimLaneColumn
              key={stage.id}
              stage={stage}
              records={records}
              lane={lane}
              onCardClick={onCardClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN FULL FLOW VIEW COMPONENT
// ============================================

export const FullFlowView = ({ users, teams, markets, onRefresh }) => {
  const navigate = useNavigate();
  const [allRecords, setAllRecords] = useState([]);
  const [shadowCards, setShadowCards] = useState([]); // Sales shadow cards
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterMarket, setFilterMarket] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showNoSale, setShowNoSale] = useState(false);
  const [showSalesTracking, setShowSalesTracking] = useState(true); // Toggle for Sales Tracking lane

  // Fetch all records (leads + jobs + sales shadow cards)
  const fetchAllRecords = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const [leadsRes, jobsRes, shadowCardsRes] = await Promise.all([
        axios.get(`${API}/leads?limit=10000`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs?limit=5000`, { headers: { Authorization: `Bearer ${token}` } }),
        // Fetch sales shadow cards for Full Flow View
        axios.get(`${API}/sales-workflow/pipeline-items?include_archived=true`, { 
          headers: { Authorization: `Bearer ${token}` } 
        }).catch(() => ({ data: { items: [] } }))
      ]);
      
      console.log(`Full Flow View: Fetched ${leadsRes.data?.length || 0} leads, ${jobsRes.data?.length || 0} jobs`);
      
      const leads = (leadsRes.data || []).map(l => ({
        id: l.id,
        contact_name: `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.company_name || 'Unknown',
        customer_name: l.company_name,
        phone: l.phone,
        email: l.email,
        address: l.address,
        stage: l.status || l.stage || 'new',
        status: l.status || l.stage || 'new',
        priority: l.priority || 'medium',
        assigned_to: l.assigned_to,
        market_id: l.market_id,
        team_id: l.team_id,
        estimated_value: l.estimated_value,
        created_at: l.created_at,
        stage_changed_at: l.updated_at,
        recordType: 'lead',
        bp_status_name: l.bp_status_name,
      }));
      
      const jobs = (jobsRes.data || []).map(j => ({
        id: j.id,
        contact_name: j.customer_name,
        customer_name: j.customer_name,
        phone: j.customer_phone,
        email: j.customer_email,
        address: j.address,
        stage: j.stage || j.status || 'lead_assigned',
        status: j.stage || j.status || 'lead_assigned',
        priority: j.priority || 'medium',
        assigned_to: j.assigned_to,
        market_id: j.market_id,
        team_id: j.assigned_team,
        estimated_value: j.estimated_value,
        created_at: j.created_at,
        stage_changed_at: j.updated_at,
        recordType: 'project',
        sales_rep_name: j.sales_rep_name,
        source_lead_id: j.source_lead_id,
        bp_status_name: j.bp_status_name,
      }));
      
      // Sales Shadow Cards (read-only in Full Flow View)
      const shadowCards = (shadowCardsRes.data?.items || []).map(s => ({
        id: `shadow-${s.id}`,
        shadow_id: s.id,
        contact_name: s.customer_name,
        customer_name: s.customer_name,
        phone: s.customer_phone,
        email: s.customer_email,
        address: s.address,
        stage: s.sales_tracking_stage, // sold, completed, archived
        status: s.sales_tracking_stage,
        priority: 'medium',
        assigned_to: s.sales_rep_id,
        sales_rep_name: s.sales_rep_name,
        estimated_value: s.contract_value || s.estimated_value,
        created_at: s.sold_date,
        sold_year: s.sold_year,
        recordType: 'shadow',
        isShadowCard: true,
        project_id: s.project_id,
        lead_id: s.lead_id,
      }));
      
      setAllRecords([...leads, ...jobs]);
      setShadowCards(shadowCards);
    } catch (err) {
      console.error('Failed to fetch records:', err);
      toast.error('Failed to load full flow data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllRecords();
  }, [fetchAllRecords]);

  // Handle card click - navigate to workspace with correct source/mode
  const handleCardClick = (record, lane) => {
    let source = lane;
    let mode = 'leads';
    
    if (lane === 'sales') {
      // Determine if it's a lead or project based on stage
      const projectStages = ['demo_sold', 'sold', 'production', 'signed'];
      if (projectStages.includes(record.stage) || record.recordType === 'project') {
        mode = 'projects';
      }
    } else if (lane === 'ops') {
      mode = 'projects';
    }
    
    navigate(`/workspace/records?source=${source}&mode=${mode}&id=${record.id}`);
  };

  // Handle drag end - only allow within same lane
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    
    if (!destination) return;
    
    // Parse lane and stage from droppable IDs
    const [sourceLane, sourceStage] = source.droppableId.split('-');
    const [destLane, destStage] = destination.droppableId.split('-');
    
    // Only allow drag within the same lane
    if (sourceLane !== destLane) {
      toast.error('Use actions to move records between departments');
      return;
    }
    
    if (sourceStage === destStage) return;
    
    // Get record ID from draggable
    const recordId = draggableId.replace(`${sourceLane}-`, '');
    const record = allRecords.find(r => r.id === recordId);
    
    if (!record) return;
    
    // Optimistic update
    setAllRecords(prev => prev.map(r => 
      r.id === recordId ? { ...r, stage: destStage, status: destStage } : r
    ));
    
    try {
      const token = localStorage.getItem('access_token');
      const endpoint = record.recordType === 'lead' 
        ? `${API}/leads/${recordId}`
        : `${API}/jobs/${recordId}`;
      
      await axios.patch(endpoint, { 
        status: destStage, 
        stage: destStage 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Moved to ${destStage.replace(/_/g, ' ')}`);
    } catch (err) {
      // Revert on error
      setAllRecords(prev => prev.map(r => 
        r.id === recordId ? record : r
      ));
      toast.error('Failed to update stage');
    }
  };

  // Filter records
  const filteredRecords = allRecords.filter(record => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      record.contact_name?.toLowerCase().includes(search) ||
      record.customer_name?.toLowerCase().includes(search) ||
      record.phone?.includes(search) ||
      record.email?.toLowerCase().includes(search) ||
      record.address?.toLowerCase().includes(search);
    
    const matchesAssignedTo = filterAssignedTo === 'all' || record.assigned_to === filterAssignedTo;
    const matchesTeam = filterTeam === 'all' || record.team_id === filterTeam;
    const matchesMarket = filterMarket === 'all' || record.market_id === filterMarket;
    const matchesPriority = filterPriority === 'all' || record.priority === filterPriority;
    
    return matchesSearch && matchesAssignedTo && matchesTeam && matchesMarket && matchesPriority;
  });

  // Separate records by lane
  const callCenterRecords = filteredRecords.filter(r => getLane(r) === 'callcenter');
  const salesRecords = filteredRecords.filter(r => getLane(r) === 'sales');
  const opsRecords = filteredRecords.filter(r => getLane(r) === 'ops');

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Loading full flow view...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Global Filters */}
      <Card className="bg-gradient-to-r from-slate-700 to-slate-800 text-white">
        <CardContent className="pt-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div>
              <Label className="text-[10px] text-white/70">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-white/50" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-7 h-8 text-xs bg-white/10 border-white/20 text-white placeholder:text-white/50"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[10px] text-white/70">Assigned To</Label>
              <Select value={filterAssignedTo} onValueChange={setFilterAssignedTo}>
                <SelectTrigger className="h-8 text-xs bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users?.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] text-white/70">Team</Label>
              <Select value={filterTeam} onValueChange={setFilterTeam}>
                <SelectTrigger className="h-8 text-xs bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Teams</SelectItem>
                  {teams?.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] text-white/70">Market</Label>
              <Select value={filterMarket} onValueChange={setFilterMarket}>
                <SelectTrigger className="h-8 text-xs bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Markets</SelectItem>
                  {markets?.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] text-white/70">Priority</Label>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="h-8 text-xs bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowNoSale(!showNoSale)}
                className="h-8 text-xs border-white/20 text-white hover:bg-white/10"
              >
                {showNoSale ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                No Sale
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchAllRecords}
                className="h-8 px-2 border-white/20 text-white hover:bg-white/10"
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Swimlanes */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="space-y-3">
          {/* Lane A: Call Center Intake */}
          <SwimLaneRow
            title="Call Center Intake"
            subtitle={`${callCenterRecords.length} records`}
            badge="Start Here"
            stages={CALL_CENTER_STAGES}
            records={callCenterRecords}
            lane="callcenter"
            onCardClick={handleCardClick}
            showNoSale={showNoSale}
            color="border-slate-300"
          />
          
          {/* Static Flow Connector: Call Center → Sales */}
          <div className="flex items-center justify-center py-1">
            <div className="flex items-center gap-2 px-4 py-1 bg-slate-100 dark:bg-slate-800 rounded-full text-xs text-muted-foreground">
              <span className="text-slate-400">↓</span>
              <span>Lead Issued → Sales</span>
            </div>
          </div>

          {/* Lane B: Sales Flow */}
          <SwimLaneRow
            title="Sales Flow"
            subtitle={`${salesRecords.length} records`}
            badge=""
            stages={SALES_STAGES}
            records={salesRecords}
            lane="sales"
            onCardClick={handleCardClick}
            showNoSale={showNoSale}
            color="border-blue-300"
          />
          
          {/* Static Flow Connector: Sales → Ops */}
          <div className="flex items-center justify-center py-1">
            <div className="flex items-center gap-2 px-4 py-1 bg-slate-100 dark:bg-slate-800 rounded-full text-xs text-muted-foreground">
              <span className="text-slate-400">↓</span>
              <span>Job Sold → Operations</span>
            </div>
          </div>

          {/* Lane C: Production Flow */}
          <SwimLaneRow
            title="Production Flow"
            subtitle={`${opsRecords.length} records`}
            badge=""
            stages={OPS_STAGES}
            records={opsRecords}
            lane="ops"
            onCardClick={handleCardClick}
            showNoSale={showNoSale}
            color="border-amber-300"
          />

          {/* Sales Shadow Tracking Lane (Sales-only view of sold → completed → archived) */}
          {showSalesTracking && shadowCards.length > 0 && (
            <>
              {/* Static Flow Connector */}
              <div className="flex items-center justify-center py-1">
                <div className="flex items-center gap-2 px-4 py-1 bg-emerald-100 dark:bg-emerald-900/30 rounded-full text-xs text-emerald-700 dark:text-emerald-300">
                  <span>📊</span>
                  <span>Sales Tracking (Read-Only)</span>
                </div>
              </div>

              {/* Sales Tracking Lane - Read-only shadow cards */}
              <div className="border-2 border-dashed border-emerald-300 dark:border-emerald-700 rounded-lg p-2 bg-emerald-50/50 dark:bg-emerald-900/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                      Sales Tracking
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {shadowCards.length} shadow cards (sales attribution)
                    </span>
                  </div>
                  <Badge variant="secondary" className="text-xs">Read-Only</Badge>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {SALES_TRACKING_STAGES.map(stage => {
                    const stageCards = shadowCards.filter(c => c.stage === stage.id);
                    return (
                      <div key={stage.id} className="flex-shrink-0 min-w-[180px] max-w-[200px]">
                        <div className={`${stage.color} rounded-t-lg px-2 py-1 flex items-center gap-1`}>
                          {stage.icon && <stage.icon className="h-3 w-3 text-white" />}
                          <span className="text-xs font-medium text-white">{stage.name}</span>
                          <Badge variant="secondary" className="ml-auto text-[9px] px-1 bg-white/20">
                            {stageCards.length}
                          </Badge>
                        </div>
                        <div className="bg-slate-100 dark:bg-slate-900 rounded-b-lg p-1.5 min-h-[100px] max-h-[150px] overflow-y-auto">
                          {stageCards.slice(0, 5).map(card => (
                            <div 
                              key={card.id}
                              className="bg-white dark:bg-slate-800 rounded-lg p-2 mb-1.5 shadow-sm border border-emerald-200 dark:border-emerald-800 cursor-default"
                            >
                              <div className="text-xs font-medium truncate">{card.contact_name}</div>
                              <div className="text-[10px] text-muted-foreground truncate">{card.address}</div>
                              <div className="flex items-center justify-between mt-1">
                                <span className="text-[9px] text-emerald-600 dark:text-emerald-400">
                                  {card.sales_rep_name || 'Unassigned'}
                                </span>
                                <span className="text-[9px] text-muted-foreground">
                                  {card.sold_year}
                                </span>
                              </div>
                            </div>
                          ))}
                          {stageCards.length > 5 && (
                            <div className="text-[9px] text-center text-muted-foreground py-1">
                              +{stageCards.length - 5} more
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </DragDropContext>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground pt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 border-l-4 border-l-red-500 bg-white rounded"></div>
          <span>High Priority</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 border-l-4 border-l-yellow-500 bg-white rounded"></div>
          <span>Medium</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 border-l-4 border-l-green-500 bg-white rounded"></div>
          <span>Low</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge variant="secondary" className="text-[8px] px-1 py-0">Project</Badge>
          <span>Converted to Project</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 border border-emerald-500 bg-emerald-100 dark:bg-emerald-900/30 rounded"></div>
          <span>Sales Shadow</span>
        </div>
      </div>
    </div>
  );
};

export default FullFlowView;
